import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';

const SuperAdminAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ shopId: '', categoryId: '', district: '', status: '', isBoosted: '' });
  const [shops, setShops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [formData, setFormData] = useState({
    categoryId: '',
    title: '',
    description: '',
    mainImageUrl: '',
    subImageUrls: '',
    durationDays: 10
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchShops();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchAds();
  }, [filters, pagination.page]);

  const fetchShops = async () => {
    try {
      const response = await api.get('/api/super/shops?limit=1000');
      setShops(response.data.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/super/categories');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAds = async () => {
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      };
      const response = await api.get('/api/super/ads', { params });
      setAds(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error fetching ads' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (adId, newStatus) => {
    try {
      await api.patch(`/api/super/ads/${adId}/status`, { status: newStatus });
      setMessage({ type: 'success', text: 'Ad status updated successfully' });
      fetchAds();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating ad status' });
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      categoryId: ad.categoryId?._id || ad.categoryId || '',
      title: ad.title || '',
      description: ad.description || '',
      mainImageUrl: ad.mainImageUrl || '',
      subImageUrls: Array.isArray(ad.subImageUrls) ? ad.subImageUrls.join('\n') : '',
      durationDays: ad.durationDays || 10
    });
    setShowEditModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleUpdateAd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const payload = {
        ...formData,
        subImageUrls: formData.subImageUrls 
          ? formData.subImageUrls.split('\n').filter(url => url.trim())
          : []
      };

      await api.put(`/api/super/ads/${editingAd._id}`, payload);
      setMessage({ type: 'success', text: 'Ad updated successfully!' });
      setShowEditModal(false);
      fetchAds();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error updating ad'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (ad) => {
    setEditingAd(ad);
    setShowDeleteConfirm(true);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.delete(`/api/super/ads/${editingAd._id}`);
      setMessage({ type: 'success', text: 'Ad deleted successfully!' });
      setShowDeleteConfirm(false);
      fetchAds();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error deleting ad'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <div className="container">
        <h1 className="page-title">Ads Management</h1>

        {message.text && (
          <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
            {message.text}
          </div>
        )}

        <div className="filters">
          <div className="form-group">
            <label>Shop</label>
            <select
              value={filters.shopId}
              onChange={(e) => setFilters({ ...filters, shopId: e.target.value })}
            >
              <option value="">All Shops</option>
              {shops.map(shop => (
                <option key={shop._id} value={shop._id}>{shop.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Category</label>
            <select
              value={filters.categoryId}
              onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat._id} value={cat._id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>District</label>
            <input
              type="text"
              value={filters.district}
              onChange={(e) => setFilters({ ...filters, district: e.target.value })}
              placeholder="District"
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
          <div className="form-group">
            <label>Boosted</label>
            <select
              value={filters.isBoosted}
              onChange={(e) => setFilters({ ...filters, isBoosted: e.target.value })}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Shop</th>
                <th>Category</th>
                <th>District</th>
                <th>Status</th>
                <th>Boosted</th>
                <th>End Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ads.map(ad => (
                <tr key={ad._id}>
                  <td>{ad.title}</td>
                  <td>{ad.shopId?.name || 'N/A'}</td>
                  <td>{ad.categoryId?.name || 'N/A'}</td>
                  <td>{ad.district}</td>
                  <td>{ad.status}</td>
                  <td>{ad.isBoosted ? 'Yes' : 'No'}</td>
                  <td>{new Date(ad.endDate).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(ad)}
                        style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(ad)}
                        style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                      >
                        Delete
                      </button>
                      <select
                        value={ad.status}
                        onChange={(e) => handleStatusChange(ad._id, e.target.value)}
                        className="btn btn-sm"
                        style={{ padding: '4px 8px', fontSize: '0.85rem' }}
                      >
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="deleted">Deleted</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.pages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Ad Modal */}
      {showEditModal && editingAd && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Ad</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateAd}>
              {message.text && (
                <div className={`alert alert-${message.type}`} style={{ margin: '15px' }}>
                  {message.text}
                </div>
              )}
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Main Image URL *</label>
                <input
                  type="url"
                  value={formData.mainImageUrl}
                  onChange={(e) => setFormData({ ...formData, mainImageUrl: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Sub Image URLs (one per line)</label>
                <textarea
                  value={formData.subImageUrls}
                  onChange={(e) => setFormData({ ...formData, subImageUrls: e.target.value })}
                  rows="4"
                  placeholder="Enter image URLs, one per line"
                />
              </div>
              <div className="form-group">
                <label>Duration (Days) *</label>
                <input
                  type="number"
                  value={formData.durationDays}
                  onChange={(e) => setFormData({ ...formData, durationDays: parseInt(e.target.value) })}
                  required
                  min="1"
                  max="10"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', padding: '15px' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Update Ad'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && editingAd && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Delete Ad</h2>
              <button className="close-btn" onClick={() => setShowDeleteConfirm(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              {message.text && (
                <div className={`alert alert-${message.type}`} style={{ marginBottom: '15px' }}>
                  {message.text}
                </div>
              )}
              <p>Are you sure you want to delete this ad?</p>
              <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', margin: '15px 0' }}>
                <p><strong>Title:</strong> {editingAd.title}</p>
                <p><strong>Shop:</strong> {editingAd.shopId?.name || 'N/A'}</p>
                <p><strong>Status:</strong> {editingAd.status}</p>
              </div>
              <p style={{ color: '#dc3545', fontWeight: '600' }}>
                This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={saving}
                >
                  {saving ? 'Deleting...' : 'Delete Ad'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminAds;

